import { PhoneNumber } from 'google-libphonenumber';
import validator from 'validator';

/**
 * Validation utilities for admin forms and data processing
 */

/**
 * Validates email address format
 */
export const isValidEmail = (email: string): boolean => {
  return validator.isEmail(email);
};

/**
 * Phone number validation result interface
 */
export interface PhoneValidationResult {
  isValid: boolean;
  formatted?: string;
  national?: string;
  international?: string;
  e164?: string;
  phoneNumber?: PhoneNumber;
  countryCode?: string;
  nationalNumber?: string;
}

/**
 * Phone number formatting options
 */
export interface PhoneFormatOptions {
  format?: 'international' | 'national' | 'e164';
  countryCode?: string;
}

/**
 * Validates required field
 */
export const isRequired = (value: any): boolean => {
  if (typeof value === 'string') {
    return value.trim().length > 0;
  }
  return value !== null && value !== undefined;
};

/**
 * Validates minimum length
 */
export const hasMinLength = (value: string, minLength: number): boolean => {
  return value.length >= minLength;
};

/**
 * Validates maximum length
 */
export const hasMaxLength = (value: string, maxLength: number): boolean => {
  return value.length <= maxLength;
};

/**
 * Validates password strength
 */
export const isStrongPassword = (password: string): boolean => {
  return validator.isStrongPassword(password, {
    minLength: 8,
    minLowercase: 1,
    minUppercase: 1,
    minNumbers: 1,
    minSymbols: 1,
  });
};

/**
 * Validates URL format
 */
export const isValidUrl = (url: string): boolean => {
  return validator.isURL(url);
};

/**
 * Validates numeric value
 */
export const isNumeric = (value: string): boolean => {
  return validator.isNumeric(value);
};

/**
 * Validates decimal number with specific precision
 */
export const isValidDecimal = (
  value: string,
  options: { precision?: number; scale?: number } = {}
): boolean => {
  const { precision = 10, scale = 2 } = options;
  const regex = new RegExp(
    `^\\d{1,${precision - scale}}(\\.\\d{1,${scale}})?$`
  );
  return regex.test(value);
};

/**
 * Validates tax ID format (simple validation)
 */
export const isValidTaxId = (taxId: string): boolean => {
  // Remove any non-alphanumeric characters
  const cleaned = taxId.replace(/[^a-zA-Z0-9]/g, '');
  // Basic length check (adjust based on requirements)
  return cleaned.length >= 9 && cleaned.length <= 15;
};

/**
 * Validates address format
 */
export const isValidAddress = (address: {
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}): boolean => {
  return !!(
    address.street &&
    address.city &&
    address.postalCode &&
    address.country
  );
};

/**
 * Validation error messages
 */
export const validationMessages = {
  required: 'This field is required',
  email: 'Please enter a valid email address',
  phone: 'Please enter a valid phone number',
  minLength: (min: number) => `Must be at least ${min} characters`,
  maxLength: (max: number) => `Must be no more than ${max} characters`,
  strongPassword:
    'Password must contain at least 8 characters with uppercase, lowercase, number, and symbol',
  url: 'Please enter a valid URL',
  numeric: 'Please enter a valid number',
  decimal: 'Please enter a valid decimal number',
  taxId: 'Please enter a valid tax ID',
  address: 'Please provide a complete address',
};
